import * as React from 'react';

export type ExperienceMode = 'modern' | 'classic';

interface ExperienceContextValue {
  experience: ExperienceMode;
  setExperience: (mode: ExperienceMode) => void;
  isModern: boolean;
  isClassic: boolean;
  bannerVisible: boolean;
  setBannerVisible: (visible: boolean) => void;
}

const STORAGE_KEY = 'bb-provisioning-ui-experience';

const ExperienceContext = React.createContext<ExperienceContextValue>({
  experience: 'modern',
  setExperience: () => {},
  isModern: true,
  isClassic: false,
  bannerVisible: true,
  setBannerVisible: () => {},
});

export const useExperience = () => React.useContext(ExperienceContext);

export const ExperienceProvider: React.FC = ({ children }) => {
  const [experience, setExperienceState] = React.useState<ExperienceMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'classic' || stored === 'modern') {
        return stored;
      }
    } catch {
      // localStorage not available
    }
    return 'modern';
  });

  const [bannerVisible, setBannerVisible] = React.useState(true);

  const setExperience = React.useCallback((mode: ExperienceMode) => {
    setExperienceState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage not available
    }
  }, []);

  const value = React.useMemo(
    () => ({
      experience,
      setExperience,
      isModern: experience === 'modern',
      isClassic: experience === 'classic',
      bannerVisible,
      setBannerVisible,
    }),
    [experience, setExperience, bannerVisible],
  );

  return <ExperienceContext.Provider value={value}>{children}</ExperienceContext.Provider>;
};
